import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import {
  certifyCandidacy, clearCandidacy, disqualifyCandidacy, getCandidacy,
  nominateCandidacy, withdrawCandidacy, type Candidacy,
} from './api'
import { NominationChip, PartyChip, label } from './vocab'
import { CandidacyForm } from './CandidacyForm'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * One candidacy, and the ladder it can move along.
 *
 * ## Every rung is its own action
 *
 * Nominate, clear, certify, withdraw, disqualify. Not a status dropdown: each carries a
 * different authority on the server and different required evidence, and one control that
 * did all five would be guarded by whichever authority the endpoint happened to demand.
 *
 * ## Every action is offered
 *
 * Including to somebody who may not hold its authority. The server refuses with a sentence
 * naming what is missing, and that sentence teaches; a hidden button does not, and makes an
 * ungranted permission indistinguishable from a missing feature.
 */
export function CandidacyDrawer({
  id, onClose, onChanged,
}: {
  id: string | null
  onClose: () => void
  onChanged?: () => void | Promise<void>
}) {
  const [row, setRow] = useState<Candidacy | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [action, setAction] = useState<Rung | null>(null)
  const [editing, setEditing] = useState(false)

  // `id` is listed. With `[]` the drawer would bind to the null it saw before it was opened
  // and stay empty for every row after the first.
  const load = useCallback(async () => {
    if (!id) { setRow(null); return }
    setLoading(true)
    setError(null)
    try {
      setRow(await getCandidacy(id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  const after = useCallback(async () => {
    await load()
    await onChanged?.()
  }, [load, onChanged])

  return (
    <Drawer open={id !== null} onClose={onClose} width="lg"
      title={row ? row.ballotName : 'Candidacy'}
      subtitle={row ? `${label(row.seatTypeCode)} · ${row.unitName ?? '—'}` : undefined}>
      {loading && !row && <LoadingBlock label="Loading the candidacy…" />}
      <ErrorStrip message={error} />

      {row && (
        <>
          <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-border">
            <NominationChip code={row.nominationStatusCode} />
            <PartyChip name={row.partyName} shortName={row.partyShortName}
              colourHex={row.partyColourHex} independent={row.independent} />
            {row.onBallot && (
              <span className="text-erp-xs text-emerald-800 dark:text-emerald-300">
                On the ballot
              </span>
            )}
          </div>

          <LadderBar row={row} onPick={(r) => { setError(null); setAction(r) }}
            onEdit={() => setEditing(true)} />

          <DrawerSection title="On the paper">
            <DrawerField label="Ballot name" value={row.ballotName} />
            <DrawerField label="In the register as" value={row.candidateName} />
            <DrawerField label="Ballot order" value={row.ballotOrder} />
            <DrawerField label="Ballot number" value={row.ballotNumber} mono />
            <DrawerField label="Running mate" value={row.runningMateName} />
          </DrawerSection>

          <DrawerSection title="The race">
            <DrawerField label="Election" value={row.electionName} />
            <DrawerField label="Seat" value={label(row.seatTypeCode)} />
            <DrawerField label="Contested at" value={label(row.requiredLevel)} />
            <DrawerField label="Where" value={row.unitName} />
          </DrawerSection>

          <DrawerSection title="The ticket">
            {/* Spelled out, because it is the single most misread field here. */}
            <DrawerField label="Party" value={row.independent
              ? 'Independent'
              : (row.partyName ?? 'none recorded')} />
            <DrawerField label="Coalition" value={row.coalitionName} />
          </DrawerSection>
          <p className="px-4 pb-3 text-erp-xs text-muted-foreground">
            This is the party <strong>as at nomination</strong> — what the ballot said. It does
            not follow the candidate if they change party later.
          </p>

          <DrawerSection title="Nomination">
            <DrawerField label="Status" value={label(row.nominationStatusCode)} />
            <DrawerField label="Certificate" value={row.certificateNumber} mono />
            <DrawerField label="Nominated" value={onDate(row.nominatedOn)} />
            <DrawerField label="Cleared" value={onDate(row.clearedOn)} />
            <DrawerField label="Certificate issued" value={onDate(row.certificateIssuedOn)} />
            <DrawerField label="Withdrawn" value={row.withdrawnAt
              ? `${new Date(row.withdrawnAt).toLocaleString()} — ${row.withdrawnReason}` : null} />
            <DrawerField label="Disqualified" value={row.disqualifiedAt
              ? `${new Date(row.disqualifiedAt).toLocaleString()} — ${row.disqualifiedReason}` : null} />
          </DrawerSection>

          <DrawerSection title="Provenance">
            <DrawerField label="Source" value={label(row.sourceCode)} />
            <DrawerField label="Import batch" value={row.importBatchId} mono />
            <DrawerField label="Notes" value={row.notes} />
          </DrawerSection>

          <LadderDialog rung={action} row={row} onClose={() => setAction(null)}
            onDone={async () => { setAction(null); await after() }}
            onError={setError} />

          <CandidacyForm open={editing} electionId={row.electionId} existing={row}
            onClose={() => setEditing(false)}
            onSaved={async () => { setEditing(false); await after() }} />
        </>
      )}
    </Drawer>
  )
}

type Rung = 'nominate' | 'clear' | 'certify' | 'withdraw' | 'disqualify'

const RUNG_LABEL: Record<Rung, string> = {
  nominate: 'Nominate', clear: 'Clear for the ballot', certify: 'Issue the certificate',
  withdraw: 'Withdraw', disqualify: 'Disqualify',
}

const RUNG_DESCRIPTION: Record<Rung, string> = {
  nominate: 'Records that the nomination papers were filed.',
  clear: 'Clears the candidacy for the ballot. It may now appear on a paper.',
  certify: 'Issues the nomination certificate. Its number is required — that number is what '
    + 'the status asserts. Ballot order is assigned now if it has not been.',
  withdraw: 'The candidate stands down. Needs a reason; the row is kept either way.',
  disqualify: 'Removes the candidacy from the ballot. Needs a reason — this is the only way '
    + 'out of a certified candidacy, and the one a petition will ask about.',
}

function LadderBar({
  row, onPick, onEdit,
}: {
  row: Candidacy
  onPick: (r: Rung) => void
  onEdit: () => void
}) {
  /*
     What is OFFERED mirrors the server's transition table, so a user is not invited to make
     a move that is always a 409. It is not a permission guess — every button here is shown
     regardless of authority, and the refusal speaks.
  */
  const status = row.nominationStatusCode
  const rungs: Rung[] =
    status === 'PROSPECTIVE' ? ['nominate', 'withdraw', 'disqualify']
      : status === 'NOMINATED' ? ['clear', 'withdraw', 'disqualify']
        : status === 'CLEARED' ? ['certify', 'withdraw', 'disqualify']
          : status === 'CERTIFICATE_ISSUED' ? ['disqualify']
            : []

  return (
    <div className="px-4 py-2 flex flex-wrap gap-1.5 border-b border-border">
      {rungs.map((r) => (
        <button key={r} type="button" onClick={() => onPick(r)}
          className="erp-btn h-8 px-2.5 text-erp-xs">
          {RUNG_LABEL[r]}
        </button>
      ))}
      {status !== 'CERTIFICATE_ISSUED' && (
        <button type="button" onClick={onEdit} className="erp-btn h-8 px-2.5 text-erp-xs">
          Amend
        </button>
      )}
      {rungs.length === 0 && (
        <span className="text-erp-xs text-muted-foreground py-1.5">
          This candidacy is {label(status).toLowerCase()} — nothing moves it further.
        </span>
      )}
    </div>
  )
}

function LadderDialog({
  rung, row, onClose, onDone, onError,
}: {
  rung: Rung | null
  row: Candidacy
  onClose: () => void
  onDone: () => void | Promise<void>
  onError: (m: string | null) => void
}) {
  const [certificateNumber, setCertificateNumber] = useState('')
  const [reason, setReason] = useState('')
  const [on, setOn] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!rung) return
    setCertificateNumber('')
    setReason('')
    setOn('')
  }, [rung])

  const needsReason = rung === 'withdraw' || rung === 'disqualify'
  const needsCertificate = rung === 'certify'
  const ready = (!needsReason || reason.trim().length > 0)
    && (!needsCertificate || certificateNumber.trim().length > 0)

  const run = async () => {
    if (!rung) return
    setBusy(true)
    onError(null)
    const body = {
      certificateNumber: certificateNumber.trim() || null,
      reason: reason.trim() || null,
      on: on || null,
    }
    try {
      switch (rung) {
        case 'nominate': await nominateCandidacy(row.id, body); break
        case 'clear': await clearCandidacy(row.id, body); break
        case 'certify': await certifyCandidacy(row.id, body); break
        case 'withdraw': await withdrawCandidacy(row.id, body); break
        case 'disqualify': await disqualifyCandidacy(row.id, body); break
      }
      await onDone()
    } catch (err) {
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={rung !== null} onClose={onClose}
      title={rung ? RUNG_LABEL[rung] : ''}
      description={rung ? RUNG_DESCRIPTION[rung] : undefined}
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" disabled={busy || !ready} onClick={() => void run()}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {rung ? RUNG_LABEL[rung] : ''}
          </button>
        </>
      )}>
      <div className="space-y-3">
        {needsCertificate && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Certificate number — required
            </span>
            <input className="erp-input h-9 w-full font-mono" value={certificateNumber}
              onChange={(e) => setCertificateNumber(e.target.value)} />
          </label>
        )}
        {needsReason && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Reason — required, and kept on the record
            </span>
            <textarea rows={3} className="erp-input w-full py-2" value={reason}
              onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
        {!needsReason && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Date — defaults to today
            </span>
            <DatePicker value={on} onChange={setOn} className="h-9 w-full" />
          </label>
        )}
      </div>
    </Modal>
  )
}

const onDate = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString() : null)
